import { Button, Container, Table } from "@fluidattacks/design";
import type { ColumnDef } from "@tanstack/react-table";
import { Fragment, useCallback, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { changeZeroRiskFormatter } from "./change-zero-risk-formatter";
import type { IZeroRiskTableProps } from "./types";

import type { IVulnDataAttr } from "../../types";
import { useTable } from "hooks/use-table";
import { severityFormatter } from "utils/format-helpers";

const ZeroRiskTable: React.FC<IZeroRiskTableProps> = ({
  acceptanceVulns,
  isConfirmRejectZeroRiskSelected,
  setAcceptanceVulns,
}: IZeroRiskTableProps): JSX.Element => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  const handleRejectZeroRisk = (vulnInfo?: IVulnDataAttr): void => {
    if (vulnInfo) {
      const newVulnList: IVulnDataAttr[] = acceptanceVulns.map(
        (vuln: IVulnDataAttr): IVulnDataAttr =>
          vuln.id === vulnInfo.id
            ? {
                ...vuln,
                acceptance: vuln.acceptance === "REJECTED" ? "" : "REJECTED",
              }
            : vuln,
      );
      setAcceptanceVulns([...newVulnList]);
    }
  };

  const handleConfirmZeroRisk = (vulnInfo?: IVulnDataAttr): void => {
    if (vulnInfo) {
      const newVulnList: IVulnDataAttr[] = acceptanceVulns.map(
        (vuln: IVulnDataAttr): IVulnDataAttr =>
          vuln.id === vulnInfo.id
            ? {
                ...vuln,
                acceptance: vuln.acceptance === "APPROVED" ? "" : "APPROVED",
              }
            : vuln,
      );
      setAcceptanceVulns([...newVulnList]);
    }
  };

  const columns: ColumnDef<IVulnDataAttr>[] = [
    {
      accessorKey: "where",
      header: "Where",
    },
    {
      accessorKey: "specific",
      header: "Specific",
    },
    {
      accessorKey: "hacker",
      header: t(
        "searchFindings.tabVuln.handleAcceptanceModal.submittedForm.submittedTable.hacker",
      ),
    },
    {
      accessorKey: "severityThreatScore",
      cell: (cell): JSX.Element => severityFormatter(Number(cell.getValue())),
      header: "Severity",
    },
    {
      accessorKey: "acceptance",
      cell: (cell): JSX.Element =>
        changeZeroRiskFormatter(
          cell.row.original,
          handleConfirmZeroRisk,
          handleRejectZeroRisk,
        ),
      header: "Acceptance",
      minSize: 180,
    },
  ];

  const handleOnChange = useCallback(
    (newValue: "APPROVED" | "REJECTED"): (() => void) =>
      (): void => {
        const newVulnList: IVulnDataAttr[] = acceptanceVulns.map(
          (vuln: IVulnDataAttr): IVulnDataAttr => ({
            ...vuln,
            acceptance: newValue,
          }),
        );
        setAcceptanceVulns([...newVulnList]);
      },
    [acceptanceVulns, setAcceptanceVulns],
  );

  const filteredData = React.useMemo(
    (): IVulnDataAttr[] =>
      acceptanceVulns.filter((vuln): boolean =>
        Object.values(vuln).some((value): boolean =>
          String(value).toLowerCase().includes(search.toLowerCase()),
        ),
      ),
    [acceptanceVulns, search],
  );

  const { table } = useTable({
    columns,
    data: filteredData,
    id: "zero-risk-table",
    options: {
      actionsOptions: {
        onSearch: setSearch,
        search,
        searchPlaceholder: t("table.search"),
      },
      filtersMode: "client-side",
      paginationMode: "client-side",
    },
    total: filteredData.length,
  });

  const actionsContent = (
    <Fragment>
      <Button
        icon={"check"}
        onClick={handleOnChange("APPROVED")}
        variant={"ghost"}
      >
        {t(
          "searchFindings.tabDescription.handleAcceptanceModal.zeroRisk.globalSwitch.confirmAll",
        )}
      </Button>
      <Button
        icon={"xmark"}
        onClick={handleOnChange("REJECTED")}
        variant={"ghost"}
      >
        {t(
          "searchFindings.tabDescription.handleAcceptanceModal.zeroRisk.globalSwitch.rejectAll",
        )}
      </Button>
    </Fragment>
  );

  return (
    <React.StrictMode>
      {isConfirmRejectZeroRiskSelected ? (
        <Table table={table}>
          <Table.Options>
            <Table.Controls>
              <Container gap={0.5} id={"actions-container"}>
                {actionsContent}
              </Container>
            </Table.Controls>
          </Table.Options>
          <Table.Content />
          <Table.Footer />
        </Table>
      ) : undefined}
    </React.StrictMode>
  );
};

export { ZeroRiskTable };
